import { useEffect, useState } from "react";
import client, { API, formatApiErrorDetail } from "@/lib/api";
import { uploadVideoChunked } from "@/lib/upload";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { PageHeader, Card, Btn, Select, Field, Empty } from "@/components/kit";
import { Upload, Trash2, FileVideo, ImageIcon, Download } from "lucide-react";

const CATEGORIES = ["Logos", "Estrutura", "Equipe", "Vídeos", "Templates", "Eventos", "Produtos", "Serviços"];

function MediaThumb({ m }) {
  const [url, setUrl] = useState(null);
  const isImg = (m.content_type || "").startsWith("image");
  const isVideo = (m.content_type || "").startsWith("video");
  useEffect(() => {
    if (!isImg) return;
    let active = true;
    client.get(`/media/${m.id}/file`, { responseType: "blob" })
      .then((r) => { if (active) setUrl(URL.createObjectURL(r.data)); }).catch(() => {});
    return () => { active = false; };
  }, [m.id, isImg]);
  if (isImg && url) return <img src={url} alt={m.original_filename} className="h-full w-full object-cover" data-testid={`media-img-${m.id}`} />;
  if (isVideo) {
    const token = localStorage.getItem("pf_token");
    return <video src={`${API}/media/${m.id}/file?auth=${token}`} controls preload="metadata" className="h-full w-full object-cover bg-black" data-testid={`media-video-${m.id}`} />;
  }
  return <div className="h-full w-full flex items-center justify-center text-slate-300">{isImg ? <ImageIcon size={30} /> : <FileVideo size={30} />}</div>;
}

export default function Midia() {
  const { activeOrgId } = useAuth();
  const [media, setMedia] = useState([]);
  const [category, setCategory] = useState("Estrutura");
  const [filter, setFilter] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const load = () => {
    if (!activeOrgId) return;
    const url = filter ? `/media?category=${filter}` : "/media";
    client.get(url).then((r) => setMedia(r.data));
  };
  useEffect(load, [activeOrgId, filter]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isVideo = (file.type || "").startsWith("video");
    setUploading(true); setProgress(0);
    try {
      if (isVideo) {
        await uploadVideoChunked(file, { category: category === "Vídeos" ? "Vídeos" : category, onProgress: setProgress });
        toast.success("Vídeo enviado ao Object Storage");
      } else {
        const fd = new FormData();
        fd.append("file", file); fd.append("category", category);
        await client.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Enviado ao Object Storage");
      }
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setUploading(false); setProgress(0); e.target.value = ""; }
  };

  const remove = async (id) => {
    try { await client.delete(`/media/${id}`); toast.success("Removido"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title="Biblioteca de Mídia" subtitle="Logos, fotos e vídeos persistidos no Object Storage (REAL). Vídeos podem ser anexados a posts do YouTube e TikTok." />

      <Card className="mb-6">
        <div className="flex flex-wrap items-end gap-4">
          <Field label="Categoria do upload">
            <Select data-testid="media-category" value={category} onChange={(e) => setCategory(e.target.value)} className="min-w-[180px]">
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </Select>
          </Field>
          <label className="cursor-pointer">
            <span className="inline-flex items-center gap-2 pf-gradient text-white font-semibold rounded-xl px-4 py-2.5 text-sm" data-testid="media-upload-label">
              <Upload size={16} /> {uploading ? (progress ? `Enviando ${progress}%` : "Enviando...") : "Enviar arquivo"}
            </span>
            <input type="file" className="hidden" onChange={upload} disabled={uploading} data-testid="media-upload-input" accept="image/*,video/*,.pdf" />
          </label>
          <div className="ml-auto">
            <Field label="Filtrar">
              <Select data-testid="media-filter" value={filter} onChange={(e) => setFilter(e.target.value)} className="min-w-[160px]">
                <option value="">Todas</option>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </Select>
            </Field>
          </div>
        </div>
        {uploading && progress > 0 && (
          <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-slate-100" data-testid="media-upload-progress">
            <div className="h-full pf-gradient transition-all" style={{ width: `${progress}%` }} />
          </div>
        )}
      </Card>

      {media.length ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {media.map((m) => (
            <Card key={m.id} className="p-0 overflow-hidden group" data-testid={`media-card-${m.id}`}>
              <div className="aspect-square bg-slate-50"><MediaThumb m={m} /></div>
              <div className="p-3 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-xs font-medium text-slate-700 truncate">{m.original_filename}</div>
                  <div className="text-[10px] text-slate-400">{m.category}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={async () => {
                    try {
                      const r = await client.get(`/media/${m.id}/file`, { responseType: "blob" });
                      const u = URL.createObjectURL(r.data);
                      const a = document.createElement("a");
                      a.href = u; a.download = m.original_filename || "imagem"; document.body.appendChild(a); a.click(); a.remove();
                      URL.revokeObjectURL(u);
                    } catch (e) {}
                  }} className="text-slate-300 hover:text-[#3457d5]" data-testid={`media-download-${m.id}`} title="Baixar imagem"><Download size={16} /></button>
                  <button onClick={() => remove(m.id)} className="text-slate-300 hover:text-rose-500" data-testid={`media-delete-${m.id}`} title="Excluir"><Trash2 size={16} /></button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : <Empty>Nenhuma mídia. Envie logos, fotos ou vídeos.</Empty>}
    </div>
  );
}
